import { memo, useEffect, useState } from "react";

import { ReactiveVar, useReactiveVar } from "@apollo/client";
import { css } from "@emotion/react";
import useAnalytics from "@hl/shared-features/lib/features/analytics/useAnalytics";
import { Button, Stack, Text } from "@mantine/core";
import ErrorLinkContainer from "~/../../communities-app/lib/components/ErrorLinkContainer";
import { topMargin } from "~/../../communities-app/lib/styles/global/layout";
import { logError } from "~/../../shared-features/lib/services/logger";

import ModalBase from "../../../../../layout/Modal/ModalBase";
import {
  Modal,
  ModalType,
  modalVar,
  resetModalVar,
} from "../../../../../layout/Modal/modal";
import {
  GetManageTokenCollectionDocument,
  GetManageTokenMintVectorsDocument,
  useUpdateManageTokenMintVectorMutation,
  useUpdateAuctionMutation,
} from "../../queries.graphql.generated";

export interface ChangeGateModalData {
  mintVectorId?: string;
  auctionId?: string;
  newGate?: {
    id: string;
    name: string;
  };
  setGateId: (gateId: string) => void;
}

export type ChangeGateModalProps = ReactiveVar<Modal<ChangeGateModalData>>;

const ChangeGateModal = () => {
  const { trackEvent, EventType } = useAnalytics();
  const { showModal, data } = useReactiveVar(modalVar as ChangeGateModalProps);
  const [error, setError] = useState<string>("");

  const [updateMintVector, { loading, error: updateMutationError, called }] =
    useUpdateManageTokenMintVectorMutation({
      refetchQueries: [
        GetManageTokenCollectionDocument,
        GetManageTokenMintVectorsDocument,
      ],
    });

  const [
    updateAuction,
    {
      loading: loadingAuction,
      error: updateAuctionError,
      called: auctionCalled,
    },
  ] = useUpdateAuctionMutation({
    refetchQueries: [],
  });

  useEffect(() => {
    if (!loading && !updateMutationError && called) {
      resetModalVar();
    }
  }, [loading, updateMutationError, called]);

  useEffect(() => {
    if (!loadingAuction && !updateAuctionError && auctionCalled) {
      resetModalVar();
    }
  }, [loadingAuction, updateAuctionError, auctionCalled]);

  return (
    <ModalBase
      isOpen={showModal === ModalType.CHANGE_GATE}
      title={"Change gate"}
    >
      <Stack mt={5} spacing="xl">
        Access to this {data.auctionId ? "auction" : "mint"} will be updated
        immediately and{" "}
        {data.newGate?.id === ""
          ? "everyone"
          : `only people who pass ${data.newGate?.name ?? ""}`}{" "}
        will be able to {data.auctionId ? "bid on" : "mint"} this token.
        <Button
          size="xl"
          fullWidth
          loading={loading}
          onClick={async () => {
            setError("");
            try {
              if (data.mintVectorId) {
                await updateMintVector({
                  variables: {
                    data: {
                      mintVectorId: data.mintVectorId,
                      gateId: data.newGate?.id ?? "",
                    },
                  },
                });
              }
              if (data.auctionId) {
                await updateAuction({
                  variables: {
                    data: {
                      auctionId: data.auctionId,
                      gateId: data.newGate?.id ?? "",
                    },
                  },
                });
              }

              if (data.newGate?.id || data.newGate?.id === "") {
                data.setGateId(data.newGate?.id);
              }
              trackEvent(EventType.EditToken_ChangeGateSaved, {
                ...data,
              });
            } catch (e) {
              setError("Failed to save changes.");
              logError({
                e,
                message: "[ChangeGateModal] Failed to update mint vector",
              });
            }
          }}
        >
          Change gate
        </Button>
        {error && (
          <div
            css={css`
              ${topMargin("3/6px")};
            `}
          >
            <ErrorLinkContainer onClose={() => setError("")}>
              <Text>{error}</Text>
            </ErrorLinkContainer>
          </div>
        )}
      </Stack>
    </ModalBase>
  );
};

export default memo(ChangeGateModal);
